import { loadApiConfig } from "@/shared/utils/configLoader";
/**
 * Configuration service that provides type-safe access to API configuration
 */
export class ConfigService {
    constructor() {
        Object.defineProperty(this, "_apiConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_initialized", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
    }
    static getInstance() {
        if (!ConfigService.instance) {
            ConfigService.instance = new ConfigService();
        }
        return ConfigService.instance;
    }
    async initialize() {
        if (this._initialized)
            return;
        this._apiConfig = await loadApiConfig();
        this._initialized = true;
    }
    /**
     * Whether the Orbital embeds (Home dashboard section + AI assistant page)
     * are enabled. Unlike `api`, this never throws: before initialization it
     * defaults to true so unit tests and early callers keep today's behavior —
     * in the running app the config is always loaded before mount (main.ts).
     */
    get isOrbitalEnabled() {
        return this._apiConfig?.orbitalEnabled ?? true;
    }
    /** Whether a reception encounter may conclude on appointment planning (OPAGD
     * Lot 2). Never throws, for the same reason as `isOrbitalEnabled` above. */
    get isAppointmentPlanningEnabled() {
        return this._apiConfig?.appointmentPlanningEnabled ?? true;
    }
    /** See `ApiConfig.spontaneousDonorsEnabled`. Opt-in, so false before initialization. */
    get isSpontaneousDonorsEnabled() {
        return this._apiConfig?.spontaneousDonorsEnabled ?? false;
    }
    /** See `ApiConfig.businessRolesMockEnabled`. False before initialization. */
    get isBusinessRolesMockEnabled() {
        return this._apiConfig?.businessRolesMockEnabled ?? false;
    }
    /**
     * TEMPORARY (OP-6808) — sidebar entries to show, by stable menu key.
     * `undefined` means no filtering. Like `isOrbitalEnabled` this never throws:
     * before initialization it reads as unconfigured, so unit tests and early
     * callers keep today's behavior. Superseded by OP-6321.
     */
    get menuVisibleKeys() {
        return this._apiConfig?.menuVisibleKeys;
    }
    /**
     * TEMPORARY (OP-6808) — configured landing path, `"/"` when unset.
     * Never throws, for the same reason as `menuVisibleKeys`. Superseded by OP-6322.
     */
    get landingPage() {
        return this._apiConfig?.landingPage ?? "/";
    }
    /**
     * Origin of the public donor site (opublic-front) serving `/forms/{slug}`, or
     * `undefined` when no public site is configured for this environment.
     *
     * Deliberately a never-throwing getter like `isOrbitalEnabled` above, NOT a read
     * through `api`: `api` throws before `initialize()`, and `?.` guards only its
     * `undefined` return, not the throw. Components that merely *display* a public
     * link must stay mountable in unit tests that never initialize the service.
     */
    get publicFormBaseUrl() {
        return this._apiConfig?.publicFormBaseUrl;
    }
    get api() {
        if (!this._initialized) {
            throw new Error("ConfigService must be initialized before use. Call initialize() first.");
        }
        return this._apiConfig;
    }
}
export const configService = ConfigService.getInstance();
